import { useState } from "react";
import { ObjectToSync } from '../../types/types';
import JsonViewer from './JsonViewer';
import { NumberToStatus } from "../../Constants/Consts";
import { SyncItemsTypes } from "../../components/SyncManager/SyncItemsTypes";

type QueueStatus = 'retry' | 'dead-letter' | 'conflict' | undefined

function getReadableSummary(objectData: ObjectToSync): string {
    const payload = objectData.payload || {}

    if (objectData.type === SyncItemsTypes.CheckListItem) {
        const statusId = payload?.statusId
        const statusLabel = (NumberToStatus as Record<string, string>)[String(statusId)] || String(statusId || 'Unknown')
        return `Checklist - Status changed to ${statusLabel}`
    }

    if (objectData.type === SyncItemsTypes.Comment) {
        const text = payload?.commentText ? String(payload.commentText) : 'No comment text'
        const trimmed = text.length > 80 ? `${text.slice(0, 80)}...` : text
        return `Comment added - "${trimmed}"`
    }

    if (objectData.type === SyncItemsTypes.PunchlistItem) {
        const statusId = payload?.statusId
        if (statusId != null) {
            const statusLabel = (NumberToStatus as Record<string, string>)[String(statusId)] || String(statusId)
            return `Punch list - Status changed to ${statusLabel}`
        }
        return `Punch list updated`
    }

    return objectData.type
}

function getQueueBadge(status?: QueueStatus): string {
    if (status === 'conflict') return 'Conflict'
    if (status === 'dead-letter') return 'Failed'
    if (status === 'retry') return 'Retrying'
    return 'Pending'
}

export default function PendingItem({
    count,
    objectData,
    errorMessage,
    queueStatus,
    onRetry,
    onCancel,
}: {
    count?: number,
    objectData: ObjectToSync,
    errorMessage?: string,
    queueStatus?: QueueStatus,
    onRetry?: (item: ObjectToSync) => void,
    onCancel?: (item: ObjectToSync) => void,
}) {
    const [collapsed, setCollapsed] = useState(false)
    const readableSummary = getReadableSummary(objectData)
    const badgeText = getQueueBadge(queueStatus)

    return (
        <div className='flex flex-col gap-2 border-b-2 py-2'>
            <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    {count && (
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-500 text-white flex items-center justify-center text-sm">
                            {count}
                        </div>
                    )}
                    <span className="px-2 py-1 text-xs rounded bg-gray-600 text-white">{badgeText}</span>
                    <span className="font-medium">{readableSummary}</span>
                    <button
                        className="text-xs underline text-gray-700"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? 'Hide Details' : 'More Details'}
                    </button>
                </div>

                <div className="flex gap-2">
                    <button
                        className="px-2 py-1 text-xs rounded bg-orange-1000 text-white"
                        onClick={() => onRetry?.(objectData)}
                    >
                        Retry
                    </button>
                    <button
                        className="px-2 py-1 text-xs rounded bg-gray-600 text-white"
                        onClick={() => onCancel?.(objectData)}
                    >
                        Cancel Sync
                    </button>
                </div>
            </div>

            {errorMessage && (
                <div className="text-xs text-red-700">Error: {errorMessage}</div>
            )}

            {collapsed && (
                <div className="w-full">
                    <JsonViewer data={{
                        summary: readableSummary,
                        queueStatus: badgeText,
                        errorMessage,
                        syncMeta: objectData.syncMeta,
                        payload: objectData.payload,
                    }} />
                </div>
            )}
        </div>
    )
}
